import React, { useState } from 'react';
import { useLanguage } from '../context/LanguageContext';
import { projects } from '../data/mock';
import { ExternalLink, Filter } from 'lucide-react';
import { Card, CardContent } from './ui/card';
import { Button } from './ui/button';
import { Badge } from './ui/badge';

const Portfolio = () => {
  const { language, t } = useLanguage();
  const [activeFilter, setActiveFilter] = useState('all');

  const filteredProjects = activeFilter === 'all'
    ? projects
    : projects.filter(p => p.category === activeFilter);

  return (
    <section id="portfolio" className="py-24 bg-transparent">
      <div className="max-w-7xl mx-auto px-6">
        {/* Section Header */}
        <div className="text-center mb-16">
          <h2 className="text-4xl md:text-5xl font-bold text-slate-100 mb-4">
            {t.portfolio.title}
          </h2>
          <p className="text-lg text-slate-300 max-w-2xl mx-auto mb-8">
            {t.portfolio.subtitle}
          </p>

          {/* Filter Buttons */}
          <div className="flex items-center justify-center gap-3 flex-wrap">
            <Button
              variant={activeFilter === 'all' ? 'default' : 'outline'}
              onClick={() => setActiveFilter('all')}
              className={`${
                activeFilter === 'all'
                  ? 'bg-cyan-500 hover:bg-cyan-600 text-slate-950 hover:text-white'
                  : 'border-cyan-500/30 bg-slate-900/40 text-slate-200 hover:bg-slate-800 hover:border-cyan-300 hover:text-white'
              } transition-all duration-200`}
            >
              {t.portfolio.all}
            </Button>
            <Button
              variant={activeFilter === 'AI' ? 'default' : 'outline'}
              onClick={() => setActiveFilter('AI')}
              className={`${
                activeFilter === 'AI'
                  ? 'bg-cyan-500 hover:bg-cyan-600 text-slate-950 hover:text-white'
                  : 'border-cyan-500/30 bg-slate-900/40 text-slate-200 hover:bg-slate-800 hover:border-cyan-300 hover:text-white'
              } transition-all duration-200`}
            >
              {t.portfolio.ai}
            </Button>
            <Button
              variant={activeFilter === 'Full-Stack' ? 'default' : 'outline'}
              onClick={() => setActiveFilter('Full-Stack')}
              className={`${
                activeFilter === 'Full-Stack'
                  ? 'bg-cyan-500 hover:bg-cyan-600 text-slate-950 hover:text-white'
                  : 'border-cyan-500/30 bg-slate-900/40 text-slate-200 hover:bg-slate-800 hover:border-cyan-300 hover:text-white'
              } transition-all duration-200`}
            >
              {t.portfolio.fullstack}
            </Button>
          </div>
        </div>

        {/* Projects Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
          {filteredProjects.map((project) => (
            <Card
              key={project.id}
              className="group overflow-hidden hover:shadow-2xl hover:shadow-cyan-500/10 transition-all duration-300 hover:-translate-y-2 border-cyan-500/20 bg-slate-900/60 backdrop-blur-sm"
            >
              {/* Project Image */}
              <div className="relative h-56 overflow-hidden bg-gray-100">
                <img
                  src={project.image}
                  alt={project.title}
                  className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300">
                  <div className="absolute bottom-4 left-4 right-4 flex gap-2">
                    {project.liveUrl !== '#' && (
                      <a
                        href={project.liveUrl}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="flex-1"
                      >
                        <Button
                          size="sm"
                          className="w-full bg-cyan-500 text-slate-950 hover:bg-cyan-600 hover:text-white"
                        >
                          <ExternalLink className="w-4 h-4 mr-2" />
                          {t.portfolio.liveDemo}
                        </Button>
                      </a>
                    )}
                  </div>
                </div>
              </div>

              {/* Project Info */}
              <CardContent className="p-6">
                <div className="mb-3">
                  <Badge
                    variant="secondary"
                    className="bg-cyan-500/20 text-cyan-200 hover:bg-cyan-500/30"
                  >
                    {project.category}
                  </Badge>
                </div>
                <h3 className="text-xl font-bold text-slate-100 mb-2">
                  {project.title}
                </h3>
                <p className="text-slate-300 mb-4 text-sm leading-relaxed">
                  {language === 'it' ? project.description : project.descriptionEn}
                </p>
                <div className="flex flex-wrap gap-2">
                  {project.technologies.map((tech) => (
                    <span
                      key={tech}
                      className="px-3 py-1 bg-slate-800 text-cyan-100 rounded-full text-xs font-medium border border-cyan-500/20"
                    >
                      {tech}
                    </span>
                  ))}
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      </div>
    </section>
  );
};

export default Portfolio;
